import type { Theme } from '@workramp/ui';
import { Body, Stack } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { useMemo, useState } from 'react';
import { useMediaQuery } from '~/hooks';
import { I18n } from '~/common/explicit-exports';
import { ButtonsContainer, ButtonRow, Labels, ScaleButton } from './SurveyScale.styles';

export type ScaleValue = 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

interface SurveyScaleProps {
  /**
   * The maximum scale value, must be between 3 and 10
   * @default 5
   */
  maxScale?: ScaleValue;
  selectedValue?: number;
  lowLabel?: string;
  highLabel?: string;
  isEditable?: boolean;
  isMandatory?: boolean;
  showLabels?: boolean;
  onChange?: (value: number | undefined) => Promise<void>;
  toggleSubmitButtonIsDisabled?: (isDisabled: boolean) => void;
}

const isValidScale = (scale: number): scale is ScaleValue => scale >= 3 && scale <= 10;

const DEFAULT_LOW_LABEL = I18n.t('js.learner.document_object.survey.strongly_disagree');
const DEFAULT_HIGH_LABEL = I18n.t('js.learner.document_object.survey.strongly_agree');

export const SurveyScale = ({
  maxScale = 5,
  selectedValue,
  lowLabel = DEFAULT_LOW_LABEL,
  highLabel = DEFAULT_HIGH_LABEL,
  isEditable = true,
  isMandatory = true,
  showLabels = true,
  onChange,
  toggleSubmitButtonIsDisabled,
}: SurveyScaleProps) => {
  if (!isValidScale(maxScale)) {
    throw new Error('SurveyScale: maxScale must be between 3 and 10');
  }

  const theme = useTheme() as Theme;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClick = async (value: number) => {
    if (!isEditable || isSubmitting) return;

    setIsSubmitting(true);
    toggleSubmitButtonIsDisabled?.(true);
    if (value === selectedValue && !isMandatory) {
      try {
        await onChange?.(undefined);
      } finally {
        setIsSubmitting(false);
        toggleSubmitButtonIsDisabled?.(false);
      }
    } else {
      try {
        await onChange?.(value);
      } finally {
        setIsSubmitting(false);
        toggleSubmitButtonIsDisabled?.(false);
      }
    }
  };

  const buttonRows = useMemo(() => {
    const buttons = Array.from({ length: maxScale }, (_, i) => i + 1);

    if (!isMobile || maxScale <= 5) {
      return [buttons];
    }

    if (maxScale <= 8) {
      return [buttons.slice(0, 4), buttons.slice(4)];
    }

    return [buttons.slice(0, 5), buttons.slice(5)];
  }, [maxScale, isMobile]);

  const buttonWidth = useMemo(() => {
    if (!isMobile || maxScale <= 5) {
      return '100%';
    }
    if (maxScale === 6) {
      return `50%`;
    }
    if (maxScale === 7) {
      return `75%`;
    }
    if (maxScale === 9) {
      return `80%`;
    }
    return `100%`;
  }, [isMobile, maxScale]);

  return (
    <Stack size={3}>
      {isMobile && showLabels && (lowLabel || highLabel) && (
        <Stack size={2}>
          {lowLabel && (
            <Body level="sm" color="gray.600">
              0 &rarr; {lowLabel}
            </Body>
          )}
          {highLabel && (
            <Body level="sm" color="gray.600">
              {maxScale} &rarr; {highLabel}
            </Body>
          )}
        </Stack>
      )}

      <ButtonsContainer isMobile={isMobile}>
        {buttonRows.map((row, rowIndex) => (
          <ButtonRow
            key={`ButtonRow${Math.random().toString()}`}
            isMobile={isMobile}
            width={rowIndex ? buttonWidth : '100%'}
            data-testid="scale-row"
          >
            {row.map((value) => {
              const isSelected = selectedValue === value;
              return (
                <ScaleButton
                  key={value}
                  $isSelected={isSelected}
                  color={isSelected ? 'primary' : 'secondary'}
                  data-testid="scale-button"
                  isBlock
                  isDisabled={!isEditable || isSubmitting}
                  onPress={() => handleClick(value)}
                  variant={isSelected ? 'solid' : 'outline'}
                >
                  {value}
                </ScaleButton>
              );
            })}
          </ButtonRow>
        ))}
      </ButtonsContainer>

      {!isMobile && showLabels && (
        <Labels isMobile={isMobile}>
          {lowLabel && <Body level="sm">{lowLabel}</Body>}
          {highLabel && <Body level="sm">{highLabel}</Body>}
        </Labels>
      )}
    </Stack>
  );
};
